/*
 * JC collection page (sections/jc-collection.liquid).
 * - "Shop by category" scroller: arrows (disabled at the ends), mouse drag with snap.
 * - Filter pills with popovers, the mobile filter drawer, sort: results reload in place through
 *   the Section Rendering API and the address follows (pushState), so back and refresh work.
 * - Load more: appends the next 52 and keeps ?page=N in the address (replaceState). Opening a
 *   ?page=N address loads the earlier pages above, so the list is complete.
 * - Best sellers (20 shown): the Men / Women switch swaps the whole section without a reload;
 *   each category chip loads the top 20 of its own collection (sections/jc-bs-grid); sort
 *   reorders the 20 shown.
 */
(() => {
  const ROOT = '[data-jc-col]';
  const parser = new DOMParser();
  let busy = null;

  const sectionId = (el) => (el.closest(ROOT) || document.querySelector(ROOT))?.dataset.section;
  const results = (root) => root.querySelector('[data-jc-col-results]');

  function withSection(url, id) {
    const u = new URL(url, location.origin);
    u.searchParams.set('section_id', id);
    return u.toString();
  }
  function cleanUrl(url) {
    const u = new URL(url, location.origin);
    u.searchParams.delete('section_id');
    if (u.searchParams.get('page') === '1') u.searchParams.delete('page');
    if (u.searchParams.get('sort_by') === '') u.searchParams.delete('sort_by');
    return u.pathname + (u.search || '') + u.hash;
  }
  async function fetchSection(url, id) {
    if (busy) busy.abort();
    busy = new AbortController();
    const res = await fetch(withSection(url, id), { signal: busy.signal, credentials: 'same-origin' });
    if (!res.ok) throw new Error(res.status);
    const doc = parser.parseFromString(await res.text(), 'text/html');
    busy = null;
    return doc;
  }

  /* ---------- category scroller ---------- */
  function initScroller(wrap) {
    if (wrap.dataset.jcReady) return;
    wrap.dataset.jcReady = '1';
    const row = wrap.querySelector('[data-jc-col-scroller]');
    const prev = wrap.querySelector('[data-jc-col-prev]');
    const next = wrap.querySelector('[data-jc-col-next-cat]');
    if (!row) return;
    const update = () => {
      const max = row.scrollWidth - row.clientWidth;
      if (prev) prev.disabled = row.scrollLeft <= 2;
      if (next) next.disabled = row.scrollLeft >= max - 2;
    };
    const step = () => {
      const tile = row.querySelector('.jc-col-tile');
      const gap = parseFloat(getComputedStyle(row).columnGap) || 16;
      const w = tile ? tile.getBoundingClientRect().width + gap : row.clientWidth * 0.8;
      return Math.max(w, Math.floor(row.clientWidth * 0.8 / w) * w);
    };
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    prev?.addEventListener('click', () => row.scrollBy({ left: -step(), behavior: reduce ? 'auto' : 'smooth' }));
    next?.addEventListener('click', () => row.scrollBy({ left: step(), behavior: reduce ? 'auto' : 'smooth' }));
    row.addEventListener('scroll', update, { passive: true });
    new ResizeObserver(update).observe(row);
    update();

    // mouse drag (touch scrolls natively)
    let x0 = 0, s0 = 0, moved = false, down = false;
    row.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      down = true; moved = false; x0 = e.clientX; s0 = row.scrollLeft;
    });
    window.addEventListener('pointermove', (e) => {
      if (!down) return;
      const dx = e.clientX - x0;
      if (!moved && Math.abs(dx) > 4) { moved = true; row.classList.add('is-drag'); }
      if (moved) row.scrollLeft = s0 - dx;
    });
    window.addEventListener('pointerup', () => {
      if (!down) return;
      down = false;
      if (moved) {
        // let snap settle from the current spot
        row.classList.remove('is-drag');
        const swallow = (ev) => { ev.preventDefault(); ev.stopPropagation(); };
        row.addEventListener('click', swallow, { capture: true, once: true });
        setTimeout(() => row.removeEventListener('click', swallow, { capture: true }), 0);
      }
    });
    row.addEventListener('dragstart', (e) => e.preventDefault());
  }

  /* ---------- popovers ---------- */
  function closePops(except) {
    document.querySelectorAll('[data-jc-col-pill-btn][aria-expanded="true"]').forEach((b) => {
      if (b === except) return;
      b.setAttribute('aria-expanded', 'false');
      const pop = document.getElementById(b.getAttribute('aria-controls'));
      if (pop) pop.hidden = true;
    });
  }
  function placePop(pop) {
    pop.style.left = '';
    pop.style.right = '';
    const r = pop.getBoundingClientRect();
    if (r.right > document.documentElement.clientWidth - 8) {
      pop.style.left = 'auto';
      pop.style.right = '0';
    }
  }

  /* ---------- results ---------- */
  function urlFromForm(form) {
    const root = form.closest(ROOT);
    const base = results(root)?.dataset.url || form.action;
    const u = new URL(base, location.origin);
    const fd = new FormData(form);
    for (const [k, v] of fd.entries()) {
      if (v === '' || v == null) continue;
      u.searchParams.append(k, v);
    }
    const sort = root.querySelector('[data-jc-col-sort]');
    if (sort && sort.value) u.searchParams.set('sort_by', sort.value);
    return u.toString();
  }

  async function loadResults(root, url, { push = true, focus = null } = {}) {
    const box = results(root);
    if (!box) return;
    box.classList.add('is-loading');
    box.setAttribute('aria-busy', 'true');
    try {
      const doc = await fetchSection(url, root.dataset.section);
      const fresh = doc.querySelector('[data-jc-col-results]');
      if (!fresh) throw new Error('no results');
      box.replaceWith(fresh);
      if (push) history.pushState({ jcCol: true }, '', cleanUrl(url));
      if (focus) root.querySelector(focus)?.focus({ preventScroll: true });
      const bar = fresh.querySelector('[data-jc-col-bar]');
      if (bar && bar.getBoundingClientRect().top < 0) bar.scrollIntoView({ block: 'start' });
    } catch (e) {
      if (e.name === 'AbortError') return;
      location.href = cleanUrl(url);
    }
  }

  function updateMore(box, doc) {
    const fresh = doc.querySelector('[data-jc-col-results]');
    if (!fresh) return;
    const more = box.querySelector('[data-jc-col-more]');
    const nmore = fresh.querySelector('[data-jc-col-more]');
    if (more && nmore) more.replaceWith(nmore);
    ['page', 'shown'].forEach((k) => (box.dataset[k] = fresh.dataset[k]));
  }

  async function loadMore(btn) {
    const root = btn.closest(ROOT);
    const box = results(root);
    const grid = box.querySelector('[data-jc-col-grid]');
    btn.setAttribute('aria-busy', 'true');
    const href = btn.href;
    try {
      const doc = await fetchSection(href, root.dataset.section);
      const items = doc.querySelectorAll('[data-jc-col-grid] > li');
      const first = items[0];
      grid.append(...items);
      updateMore(box, doc);
      applyCat(root);
      const u = new URL(href, location.origin);
      history.replaceState(history.state, '', cleanUrl(u.toString()));
      // keyboard users land on the first new product
      if (first && btn === document.activeElement) first.querySelector('a')?.focus({ preventScroll: true });
    } catch (e) {
      if (e.name === 'AbortError') return;
      location.href = href;
    }
  }

  // opened on ?page=N: load pages 1..N-1 above, keeping the view where it is
  async function backfill(root) {
    const box = results(root);
    const page = parseInt(box?.dataset.page || '1', 10);
    if (!box || page <= 1) return;
    const grid = box.querySelector('[data-jc-col-grid]');
    if (!grid) return;
    const anchor = grid.firstElementChild;
    const top0 = anchor ? anchor.getBoundingClientRect().top : 0;
    const frag = document.createDocumentFragment();
    for (let p = 1; p < page; p += 1) {
      const u = new URL(location.href);
      u.searchParams.set('page', p);
      try {
        const res = await fetch(withSection(u.toString(), root.dataset.section), { credentials: 'same-origin' });
        const doc = parser.parseFromString(await res.text(), 'text/html');
        frag.append(...doc.querySelectorAll('[data-jc-col-grid] > li'));
      } catch (e) {
        return;
      }
    }
    grid.prepend(frag);
    applyCat(root);
    if (anchor && top0 > 0) window.scrollBy(0, anchor.getBoundingClientRect().top - top0);
  }

  /* ---------- best sellers ---------- */
  // the 20 shown, in the chosen order: '' keeps the collection's own (best-seller) order
  function applyCat(root) {
    const box = results(root);
    if (!box || box.dataset.bs == null) return;
    const grid = box.querySelector('[data-jc-col-grid]');
    const sel = box.querySelector('[data-jc-col-sort]');
    if (!grid) return;
    const how = sel ? sel.value : '';
    const num = (li, k) => parseFloat(li.dataset[k] || '0');
    const by = {
      '': (x, y) => num(x, 'i') - num(y, 'i'),
      'price-ascending': (x, y) => num(x, 'price') - num(y, 'price') || num(x, 'i') - num(y, 'i'),
      'price-descending': (x, y) => num(y, 'price') - num(x, 'price') || num(x, 'i') - num(y, 'i'),
      'created-descending': (x, y) => num(y, 'created') - num(x, 'created') || num(x, 'i') - num(y, 'i'),
    }[how] || ((x, y) => num(x, 'i') - num(y, 'i'));
    grid.append(...[...grid.children].sort(by));
  }

  function setCount(box, n) {
    const txt = `${n.toLocaleString('en-US')} ${n === 1 ? 'product' : 'products'}`;
    box.querySelectorAll('[data-jc-col-count]').forEach((el) => (el.textContent = txt));
    const shown = box.querySelector('[data-jc-col-shown]');
    if (shown) shown.textContent = `Showing ${n.toLocaleString('en-US')} of ${txt}`;
    const fill = box.querySelector('[data-jc-col-bar-fill]');
    if (fill) fill.style.width = '100%';
    box.dataset.total = box.dataset.shown = String(n);
  }

  // a category chip: the top 20 of that collection, in its own order
  const catCache = new Map();
  async function loadCat(chip) {
    const root = chip.closest(ROOT);
    const box = results(root);
    const url = chip.dataset.jcColCat;
    root.querySelectorAll('[data-jc-col-cat]').forEach((b) => {
      const on = b.dataset.jcColCat === url;
      b.classList.toggle('is-on', on);
      b.setAttribute('aria-pressed', String(on));
    });
    box.classList.add('is-loading');
    box.setAttribute('aria-busy', 'true');
    try {
      let html = catCache.get(url);
      if (!html) {
        if (busy) busy.abort();
        busy = new AbortController();
        const res = await fetch(withSection(url, 'jc-bs-grid'), { signal: busy.signal, credentials: 'same-origin' });
        if (!res.ok) throw new Error(res.status);
        html = await res.text();
        busy = null;
        catCache.set(url, html);
      }
      // a later chip click won the race
      if (chip.getAttribute('aria-pressed') !== 'true') return;
      const doc = parser.parseFromString(html, 'text/html');
      const fresh = doc.querySelector('[data-jc-bs-grid]');
      if (!fresh) throw new Error('no grid');
      const grid = box.querySelector('[data-jc-col-grid]');
      grid.replaceChildren(...fresh.children);
      setCount(box, parseInt(fresh.dataset.total || grid.children.length, 10));
      applyCat(root);
    } catch (e) {
      if (e.name !== 'AbortError') location.href = url;
    } finally {
      box.classList.remove('is-loading');
      box.removeAttribute('aria-busy');
    }
  }

  async function switchTab(link) {
    const root = link.closest(ROOT);
    if (link.classList.contains('is-on')) return;
    try {
      const doc = await fetchSection(link.href, root.dataset.section);
      const fresh = doc.querySelector(ROOT);
      if (!fresh) throw new Error('no section');
      root.replaceWith(fresh);
      history.pushState({ jcCol: true }, '', cleanUrl(link.href));
      fresh.querySelector(`[data-jc-col-tab="${link.dataset.jcColTab}"]`)?.focus({ preventScroll: true });
      init(fresh);
    } catch (e) {
      if (e.name !== 'AbortError') location.href = link.href;
    }
  }

  /* ---------- drawer ---------- */
  let lastFocus = null;
  function openDrawer(btn) {
    const d = document.getElementById(btn.getAttribute('aria-controls'));
    if (!d) return;
    lastFocus = btn;
    d.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    btn.setAttribute('aria-expanded', 'true');
    d.querySelector('.jc-col-drawer__panel')?.focus({ preventScroll: true });
  }
  function closeDrawer(d) {
    if (!d || d.hidden) return;
    d.hidden = true;
    document.documentElement.style.overflow = '';
    const btn = document.querySelector(`[aria-controls="${d.id}"]`);
    btn?.setAttribute('aria-expanded', 'false');
    (btn || lastFocus)?.focus({ preventScroll: true });
  }

  /* ---------- events (delegated, so swapped markup keeps working) ---------- */
  document.addEventListener('click', (e) => {
    const t = e.target;
    if (!t.closest || !t.closest(ROOT)) {
      closePops();
      return;
    }
    const pill = t.closest('[data-jc-col-pill-btn]');
    if (pill) {
      const open = pill.getAttribute('aria-expanded') === 'true';
      closePops(pill);
      pill.setAttribute('aria-expanded', String(!open));
      const pop = document.getElementById(pill.getAttribute('aria-controls'));
      if (pop) {
        pop.hidden = open;
        if (!open) placePop(pop);
      }
      return;
    }
    if (!t.closest('.jc-col-pop')) closePops();

    const clearGroup = t.closest('[data-jc-col-clear-group]');
    if (clearGroup) {
      const pop = clearGroup.closest('.jc-col-pop');
      pop.querySelectorAll('input[type="checkbox"]').forEach((i) => (i.checked = false));
      pop.querySelectorAll('input[type="number"]').forEach((i) => (i.value = ''));
      const form = clearGroup.closest('form');
      if (form) form.requestSubmit();
      return;
    }
    const more = t.closest('[data-jc-col-loadmore]');
    if (more) {
      e.preventDefault();
      if (more.getAttribute('aria-busy') !== 'true') loadMore(more);
      return;
    }
    const link = t.closest('[data-jc-col-link]');
    if (link) {
      e.preventDefault();
      closeDrawer(link.closest('[data-jc-col-drawer]'));
      loadResults(link.closest(ROOT), link.href);
      return;
    }
    const tab = t.closest('[data-jc-col-tab]');
    if (tab) {
      e.preventDefault();
      switchTab(tab);
      return;
    }
    const cat = t.closest('[data-jc-col-cat]');
    if (cat) {
      if (cat.getAttribute('aria-pressed') !== 'true') loadCat(cat);
      return;
    }
    const opener = t.closest('[data-jc-col-drawer-open]');
    if (opener) {
      openDrawer(opener);
      return;
    }
    const closer = t.closest('[data-jc-col-drawer-close]');
    if (closer) closeDrawer(closer.closest('[data-jc-col-drawer]'));
  });

  document.addEventListener('submit', (e) => {
    const form = e.target.closest && e.target.closest('[data-jc-col-filter-form]');
    if (!form) return;
    e.preventDefault();
    closePops();
    closeDrawer(form.closest('[data-jc-col-drawer]'));
    loadResults(form.closest(ROOT), urlFromForm(form));
  });

  document.addEventListener('change', (e) => {
    const sel = e.target.closest && e.target.closest('[data-jc-col-sort]');
    if (!sel) return;
    const root = sel.closest(ROOT);
    root.querySelectorAll('[data-jc-col-sort]').forEach((s) => (s.value = sel.value));
    // best sellers: sort the 20 shown in place
    if (results(root)?.dataset.bs != null) {
      applyCat(root);
      return;
    }
    const form = root.querySelector('.jc-col-filters[data-jc-col-filter-form]');
    let url;
    if (form) {
      url = urlFromForm(form);
    } else {
      const u = new URL(results(root).dataset.url, location.origin);
      if (sel.value) u.searchParams.set('sort_by', sel.value);
      url = u.toString();
    }
    loadResults(root, url, { focus: `#${sel.id}` });
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const openPill = document.querySelector('[data-jc-col-pill-btn][aria-expanded="true"]');
    if (openPill) {
      closePops();
      openPill.focus();
      return;
    }
    document.querySelectorAll('[data-jc-col-drawer]:not([hidden])').forEach(closeDrawer);
  });

  window.addEventListener('popstate', () => {
    const root = document.querySelector(ROOT);
    if (!root) return;
    if (root.querySelector('[data-jc-col-tab]')) {
      location.reload();
      return;
    }
    loadResults(root, location.href, { push: false });
  });

  function init(scope) {
    const roots = scope ? [scope] : document.querySelectorAll(ROOT);
    roots.forEach((root) => {
      root.querySelectorAll('[data-jc-col-cats]').forEach(initScroller);
      backfill(root);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (e) => init(e.target.querySelector(ROOT)));
})();
